import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, BookOpen, Loader2, Search } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";
import { ManualBlockRenderer } from "@/components/manuals/ManualBlockRenderer";

const sectionText = (s) =>
  [s.title, ...(s.blocks || []).flatMap((b) => [b.text || "", b.caption || "", ...(b.items || [])])].join(" ").toLowerCase();

// Clean manual-reading interface: sidebar table of contents, one section at a time, search.
export const ManualReader = ({ manualId, onBack }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setData(null);
    setError(null);
    axios
      .get(`${API}/manuals/${manualId}`, { headers: authHeaders() })
      .then((r) => {
        setData(r.data);
        setActiveId(r.data.sections?.[0]?.id || null);
      })
      .catch((err) => setError(err.response?.status === 404 ? "notfound" : "error"));
  }, [manualId]);

  const sections = data?.sections || [];
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null; // null = no filtering
    return new Set(sections.filter((s) => sectionText(s).includes(q)).map((s) => s.id));
  }, [sections, query]);

  const visible = matches ? sections.filter((s) => matches.has(s.id)) : sections;
  const active = sections.find((s) => s.id === activeId) || visible[0] || null;
  const activeIdx = active ? visible.findIndex((s) => s.id === active.id) : -1;

  if (error) {
    return (
      <div data-testid="manual-reader-error" className="rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16 text-center text-sm text-[var(--pv-text-4)]">
        {error === "notfound" ? "This manual isn't available for your account." : "Could not load the manual."}
      </div>
    );
  }
  if (!data) {
    return <p data-testid="manual-reader-loading" className="py-16 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-cyan-400" /></p>;
  }

  return (
    <div data-testid="manual-reader" className="overflow-hidden rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)]">
      <div className="border-b border-[var(--pv-border)] p-5 sm:px-7">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {onBack && (
              <button data-testid="manual-reader-back" onClick={onBack} className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-2 text-[var(--pv-text-3)] hover:text-cyan-400">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div>
              <h2 data-testid="manual-reader-title" className="font-display text-xl font-bold tracking-tight text-[var(--pv-text)]">{data.manual.title}</h2>
              {data.manual.description && <p className="mt-0.5 text-xs text-[var(--pv-text-4)]">{data.manual.description}</p>}
            </div>
          </div>
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--pv-text-4)]" />
            <input
              data-testid="manual-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search this manual…"
              className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] py-2 pl-9 pr-4 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
            />
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row">
        <aside data-testid="manual-toc" className="shrink-0 border-b border-[var(--pv-border)] p-4 md:max-h-[70vh] md:w-64 md:overflow-y-auto md:border-b-0 md:border-r">
          {visible.length === 0 ? (
            <p data-testid="manual-toc-empty" className="px-2 py-4 text-xs text-[var(--pv-text-4)]">
              {sections.length === 0 ? "No published sections yet." : "No sections match your search."}
            </p>
          ) : (
            <nav className="space-y-1">
              {visible.map((s, i) => (
                <button
                  key={s.id}
                  data-testid={`manual-toc-item-${s.id}`}
                  onClick={() => setActiveId(s.id)}
                  className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-semibold transition-colors ${
                    active?.id === s.id ? "bg-cyan-500 text-white" : "text-[var(--pv-text-3)] hover:bg-[var(--pv-subtle)] hover:text-[var(--pv-text)]"
                  }`}
                >
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold ${active?.id === s.id ? "bg-white/20 text-white" : "bg-[var(--pv-subtle)] text-[var(--pv-text-4)]"}`}>{i + 1}</span>
                  <span className="min-w-0 flex-1 truncate">{s.title}</span>
                  {s.status === "draft" && (
                    <span data-testid={`manual-toc-draft-${s.id}`} className="rounded-full border border-amber-500/30 bg-amber-500/15 px-1.5 py-0.5 text-[8px] font-bold uppercase text-amber-300">draft</span>
                  )}
                </button>
              ))}
            </nav>
          )}
        </aside>

        <div className="min-w-0 flex-1 p-6 sm:p-8 md:max-h-[70vh] md:overflow-y-auto">
          {active ? (
            <article data-testid="manual-section-content">
              <div className="mb-5 flex flex-wrap items-center gap-2">
                <BookOpen className="h-4 w-4 text-cyan-400" />
                <h3 data-testid="manual-section-title" className="font-display text-lg font-bold text-[var(--pv-text)]">{active.title}</h3>
                {active.status === "draft" && (
                  <span data-testid="manual-section-draft-badge" className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-300">Draft — only you can see this</span>
                )}
              </div>
              {active.blocks?.length ? (
                <ManualBlockRenderer blocks={active.blocks} />
              ) : (
                <p className="text-sm text-[var(--pv-text-4)]">This section has no content yet.</p>
              )}
              <div className="mt-10 flex items-center justify-between border-t border-[var(--pv-border)] pt-5">
                {activeIdx > 0 ? (
                  <button data-testid="manual-prev-section" onClick={() => setActiveId(visible[activeIdx - 1].id)} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-3)] hover:text-cyan-400">
                    <ArrowLeft className="h-3.5 w-3.5" /> {visible[activeIdx - 1].title}
                  </button>
                ) : <span />}
                {activeIdx >= 0 && activeIdx < visible.length - 1 ? (
                  <button data-testid="manual-next-section" onClick={() => setActiveId(visible[activeIdx + 1].id)} className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white hover:bg-cyan-600">
                    {visible[activeIdx + 1].title} <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : <span />}
              </div>
            </article>
          ) : (
            <p data-testid="manual-section-empty" className="py-10 text-center text-sm text-[var(--pv-text-4)]">Pick a section from the contents.</p>
          )}
        </div>
      </div>
    </div>
  );
};
